import { useEffect, useRef } from "react";
import cytoscape from "cytoscape";

export function ingredientColor(category) {
  const c = (category || "").toLowerCase();
  if (/(erva|vegeta|legum|hortali|folha)/.test(c)) return "#22C55E";      // ervas/vegetais
  if (/(fruta|[áa]cid|c[íi]tr|berry)/.test(c)) return "#F43F5E";          // frutas/ácidos
  if (/(gr[ãa]o|a[çc][úu]car|cereal|confeit|doce|massa)/.test(c)) return "#FACC15"; // grãos/açúcares
  if (/(latic|prote[íi]|carne|ferment|queijo|ovo|peix)/.test(c)) return "#D97706";  // proteicos/fermentados
  if (/(especiar|tempero|condiment)/.test(c)) return "#A855F7";           // especiarias
  return "#F97316"; // default (âmbar-laranja)
}

export function toElements(graph) {
  const nodes = (graph?.nodes || []).map((n) => ({
    data: {
      id: n.element_id,
      label: n.properties?.name || n.element_id,
      ntype: (n.labels || [])[0] || "Node",
      ...n.properties,
    },
  }));
  const edges = (graph?.edges || []).map((e) => ({
    data: {
      id: e.element_id,
      source: e.start,
      target: e.end,
      etype: e.type,
      score: e.properties?.score,
      ppm: e.properties?.concentration_ppm,
    },
  }));
  return [...nodes, ...edges];
}

const STYLE = [
  {
    selector: "node",
    style: {
      label: "data(label)",
      color: "#E5E9F0",
      "font-family": "JetBrains Mono, monospace",
      "font-size": "10px",
      "text-valign": "bottom",
      "text-margin-y": 4,
      "text-outline-color": "#0B0F17",
      "text-outline-width": 2,
      "text-wrap": "ellipsis",
      "text-max-width": "90px",
      width: 34,
      height: 34,
      "border-width": 2,
      "border-color": "#0B0F17",
      "transition-property": "border-color, border-width, background-color",
      "transition-duration": "150ms",
    },
  },
  { selector: 'node[ntype="Ingredient"]', style: { "background-color": (ele) => ingredientColor(ele.data("category")), shape: "ellipse", width: 44, height: 44 } },
  { selector: 'node[ntype="ChemicalCompound"]', style: { "background-color": "#00E5FF", shape: "round-rectangle" } },
  { selector: 'node[ntype="FlavorCategory"]', style: { "background-color": "#EC4899", shape: "diamond", width: 38, height: 38 } },
  // center node of the web is slightly larger
  { selector: "node.web-center", style: { width: 58, height: 58, "border-width": 3, "border-color": "#E05A2B", "z-index": 30 } },

  // Web threads — thin, semi-transparent, straight radial lines
  {
    selector: "edge",
    style: {
      width: 1,
      "line-color": "#2A3650",
      "curve-style": "straight",
      opacity: 0.22,
    },
  },
  { selector: 'edge[etype="PAIRS_WITH"]', style: { width: "mapData(score, 0, 1, 1, 3)", "line-color": "#7A2C55", opacity: 0.3 } },
  { selector: 'edge[etype="HAS_COMPOUND"]', style: { width: "mapData(ppm, 0, 1200, 1, 3)", "line-color": "#1E5A73", opacity: 0.28 } },
  { selector: 'edge[etype="BELONGS_TO"]', style: { "line-color": "#6B5320", "line-style": "dashed", opacity: 0.3 } },

  // Selection / highlight
  { selector: "node:selected", style: { "border-width": 4, "border-color": "#FFFFFF" } },
  { selector: "node.hl", style: { "border-width": 5, "border-color": "#FFFFFF", "background-blacken": -0.15 } },
  { selector: "node.hover", style: { "border-width": 4, "border-color": "#F8FAFC" } },

  // Active web glow (orange) — only on the active node's threads
  {
    selector: "node.web-active",
    style: {
      "border-color": "#E05A2B",
      "border-width": 4,
      "overlay-color": "#E05A2B",
      "overlay-opacity": 0.18,
      "overlay-padding": 8,
      "z-index": 40,
    },
  },
  { selector: "edge.edge-glow", style: { opacity: 1, "line-color": "#E05A2B", width: 3.5, "z-index": 25 } },
  { selector: "edge.edge-hover", style: { opacity: 1, "line-color": "#F6A06B", width: 3, "z-index": 24 } },
];

const CENTER_LEVEL = 3;
const INNER_LEVEL = 2;   // ChemicalCompound + FlavorCategory
const OUTER_LEVEL = 1;   // complementary Ingredients

function pickCenter(cy, centerId) {
  if (centerId) {
    const c = cy.getElementById(centerId);
    if (c && c.nonempty()) return c;
  }
  const ings = cy.nodes('[ntype="Ingredient"]');
  const pool = ings.nonempty() ? ings : cy.nodes();
  if (pool.empty()) return null;
  return pool.max((n) => n.degree(false)).ele;
}

function assignLevels(cy, centerId) {
  const center = pickCenter(cy, centerId);
  cy.batch(() => {
    cy.nodes().removeClass("web-center");
    cy.nodes().forEach((n) => {
      const t = n.data("ntype");
      let level = OUTER_LEVEL;
      if (center && n.id() === center.id()) level = CENTER_LEVEL;
      else if (t === "ChemicalCompound" || t === "FlavorCategory") level = INNER_LEVEL;
      n.data("level", level);
    });
    if (center) center.addClass("web-center");
  });
  return center;
}

function webLayout(animate = true) {
  return {
    name: "concentric",
    fit: true,
    padding: 70,
    startAngle: (3 / 2) * Math.PI,
    clockwise: true,
    equidistant: true,
    minNodeSpacing: 50,
    avoidOverlap: true,
    concentric: (node) => node.data("level") || OUTER_LEVEL,
    levelWidth: () => 1,
    animate,
    animationDuration: 500,
  };
}

export default function FlavorGraphCanvas({ elements, onSelect, onExpand, highlightId, layoutKey, centerId }) {
  const ref = useRef(null);
  const cyRef = useRef(null);

  const setActiveWeb = (cy, node) => {
    cy.batch(() => {
      cy.nodes().removeClass("web-active");
      cy.edges().removeClass("edge-glow");
      if (node && node.nonempty()) {
        node.addClass("web-active");
        node.connectedEdges().addClass("edge-glow");
      }
    });
  };

  useEffect(() => {
    const cy = cytoscape({
      container: ref.current,
      elements,
      style: STYLE,
      layout: { name: "preset" },
      minZoom: 0.2,
      maxZoom: 3,
    });
    cyRef.current = cy;
    assignLevels(cy, centerId);
    cy.layout(webLayout(true)).run();

    cy.on("tap", "node", (evt) => {
      onSelect && onSelect(evt.target.data());
      setActiveWeb(cy, evt.target);
    });
    cy.on("tap", (evt) => { if (evt.target === cy) setActiveWeb(cy, null); });
    cy.on("dbltap", "node", (evt) => {
      const d = evt.target.data();
      if (d.sop_raft_id) onExpand && onExpand(d.sop_raft_id);
    });
    cy.on("mouseover", "node", (e) => {
      e.target.addClass("hover");
      e.target.connectedEdges().addClass("edge-hover");
    });
    cy.on("mouseout", "node", (e) => {
      e.target.removeClass("hover");
      e.target.connectedEdges().removeClass("edge-hover");
    });
    return () => cy.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.json({ elements });
    assignLevels(cy, centerId);
    cy.layout(webLayout(false)).run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, centerId]);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.elements().removeClass("hl");
    if (highlightId) {
      const n = cy.getElementById(highlightId);
      if (n && n.length) {
        n.addClass("hl");
        setActiveWeb(cy, n);
        cy.animate({ center: { eles: n }, zoom: 1.5 }, { duration: 450 });
      }
    }
  }, [highlightId]);

  useEffect(() => {
    const cy = cyRef.current;
    if (cy && layoutKey) { assignLevels(cy, centerId); cy.layout(webLayout(true)).run(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey]);

  return <div ref={ref} data-testid="flavor-graph-canvas" style={{ width: "100%", height: "100%" }} />;
}
